import { useLocation, useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';

import { SamplekitMessage } from '../../../types/SamplekitMessage';
import { SupplierMessage } from '../../../types/SupplierMessage';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { useToasts } from '../../cdl/Toast/useToasts';
import { CreateOrderResponse } from '../../common/api/clients/order.api';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { Item } from '../../common/models/item.model';
import { ItemModel } from '../../common/models/ItemModel';
import { useOrderDelete } from '../common/hooks/useOrderDelete';
import { DirectOrderModal } from '../create-enquiry/components/DirectOrderModal';
import { FormikCreateEnquiryForm } from '../create-enquiry/components/FormikCreateEnquiryForm';
import { FormikSpotModal } from '../create-enquiry/components/FormikSpotModal';
import { useDirectOrder } from '../create-enquiry/hooks/useDirectOrder';
import { useEnquiryCreate } from '../create-enquiry/hooks/useEnquiryCreate';
import { useEnquiryUpdate } from '../create-enquiry/hooks/useEnquiryUpdate';
import { useMarkOrderRead } from '../create-enquiry/hooks/useMarkOrderRead';
import { useValidateCreateEnquiryForm } from '../create-enquiry/hooks/useValidateCreateEnquiryForm';
import { buildOrderRequest } from '../create-enquiry/util/buildOrderRequest';
import { getIncrementedBuyerReference } from '../create-enquiry/util/getIncrementedBuyerReference';
import { useOrder } from '../hooks/useOrder';
import { OrderState } from '../model/OrderModel';
import { OrderTabEnum } from '../OrderOverview/orderTabsHelper';

import { OrderMode } from './types/OrderMode';

export interface FormikCreateEnquiryValues {
    customerId: string;
    vesselId: string;
    portId: string;
    dateDelivery: string | null;
    buyerReference: string;
    agentId: string;
    items: ItemModel[];
    samplekits: SamplekitMessage[];
    testOrder: boolean;
    assignedSupplierIds: string[];
    assignedSuppliers: SupplierMessage[];
    receiverSupplierIds: string[];
    receiverContractIds: string[];
    spot: boolean;
    comment?: string;
    creationComment?: string;
    state?: OrderState;
    fileIds: string[];
    scheduleSharingEnabled?: boolean;
    orderMode: OrderMode;
}

interface CreateEnquiryProps {
    orderId?: string;
}

export const CreateEnquiryFlow = ({ orderId }: CreateEnquiryProps) => {
    const [directOrderValues, setDirectOrderValues] = useState<FormikCreateEnquiryValues | null>(null);
    const { placeDirectOrder, isPending: isPlacingDirectOrder } = useDirectOrder(orderId);
    const [spotModalOpen, setSpotModalOpen] = useState(false);
    const role = useRole();

    const { mutateAsync: createEnquiryAsync } = useEnquiryCreate();
    const { mutateAsync: updateEnquiryAsync } = useEnquiryUpdate();
    const deleteOrderMutation = useOrderDelete();
    const { mutate: markOrderRead } = useMarkOrderRead();
    const navigate = useNavigate();
    const orderDetailsQuery = useOrder(orderId);
    const location = useLocation();
    const { addErrorToast } = useToasts();
    const formikValidate = useValidateCreateEnquiryForm(orderId);

    const {
        order: orderFromState,
        vesselId: vesselIdFromState,
        customerId: customerIdFromState,
        portId: portIdFromState,
        items: itemsFromState,
    } = location.state;

    useEffect(() => {
        if (orderId && orderDetailsQuery.data && !role.isSuperAdmin() && !orderDetailsQuery.data.read) {
            markOrderRead({ orderId, read: true });
        }
    }, [orderId, markOrderRead, orderDetailsQuery.data, orderDetailsQuery.data?.read, role]);

    const isDraftEnquiry = !!orderId;
    const isCopiedFromOrder = !!orderFromState;
    const order = orderDetailsQuery.data;
    const canDeleteDraft = isDraftEnquiry && !!order;

    useDocumentTitle(isDraftEnquiry ? translate('order.createNewDraft') : translate('order.createnew'));

    const handleSubmitError = (error: AxiosError<ValidationResponse>) => {
        if (error.response?.status === 400) {
            addErrorToast(translate('order.validationFailedToast'));
            return;
        }
        addErrorToast(translate('alert.defaulterror.message'));
    };

    const handleSendEnquiry = async (values: FormikCreateEnquiryValues) => {
        if (isDraftEnquiry) {
            await updateEnquiryAsync(
                { order: buildOrderRequest(values, OrderState.OPEN), orderId },
                {
                    onSuccess: () => {
                        navigate({ to: '/order/$id', params: { id: order.id } });
                    },
                    onError: handleSubmitError,
                }
            );
        } else {
            await createEnquiryAsync(buildOrderRequest(values, OrderState.OPEN), {
                onSuccess: (order: CreateOrderResponse) => {
                    navigate({ to: '/order/$id', params: { id: order.id } });
                },
                onError: handleSubmitError,
            });
        }
    };

    const handleInitialItems = (items: ItemModel[]): ItemModel[] => {
        if (items?.length) {
            return items.map((item): ItemModel => {
                // @ts-ignore
                return new Item({
                    productId: item.productId || '',
                    units: item.units || '',
                    packType: item.packType || 'BULK',
                    unitSize: item.unitSize || 1,
                    unit: item.unit || 'L',
                    new: true,
                    deleted: false,
                    total: item.total || undefined,
                    product: item.product || undefined,
                    sourceValues: item.sourceValues || undefined,
                });
            });
        }

        return [
            // @ts-ignore
            new Item({
                productId: '',
                new: true,
                unit: 'L',
                units: '',
                packType: 'BULK',
                unitSize: 1,
                deleted: false,
            }),
        ];
    };

    const getBuyerReferenceForCopy = (isCopied: boolean, sourceBuyerReference: string): string => {
        if (isCopied && sourceBuyerReference) {
            return getIncrementedBuyerReference(sourceBuyerReference);
        }
        return sourceBuyerReference;
    };

    const initialItemsFromOrder = order?.items.length ? order.items : undefined;
    const initialItemsFromState = itemsFromState || orderFromState?.items || undefined;

    const initialValues: FormikCreateEnquiryValues = {
        orderMode: OrderMode.ENQUIRY,
        vesselId: orderFromState?.vesselId || vesselIdFromState || order?.vesselId || '',
        customerId: orderFromState?.customerId || customerIdFromState || order?.customerId || '',
        portId: orderFromState?.portId || portIdFromState || order?.portId || '',
        dateDelivery: orderFromState?.dateDelivery || order?.dateDelivery || null,
        testOrder: orderFromState?.testOrder || order?.testOrder || role.group.testCompany,
        buyerReference:
            orderFromState?.buyerReference || order?.buyerReference
                ? getBuyerReferenceForCopy(isCopiedFromOrder, orderFromState?.buyerReference || order?.buyerReference)
                : '',
        comment: orderFromState?.comment || order?.comment || '',
        items: handleInitialItems(initialItemsFromState || initialItemsFromOrder),
        samplekits: orderFromState?.samplekits || order?.samplekits || [],
        assignedSupplierIds: [],
        assignedSuppliers: [],
        receiverSupplierIds: orderFromState?.receiverSupplierIds || order?.receiverSupplierIds || [],
        receiverContractIds: orderFromState?.receiverContractIds || order?.receiverContractIds || [],
        creationComment: order?.creationComment || '',
        spot: orderFromState?.spot || order?.spot || false,
        agentId: orderFromState?.agentId || order?.agentId || '',
        state: order?.state,
        fileIds: order?.fileIds || null,
        scheduleSharingEnabled: orderFromState?.scheduleSharingEnabled ?? order?.scheduleSharingEnabled,
    };

    const onCloseClick = (dirty: boolean) => {
        if (!dirty) {
            history.back();
            return;
        }

        const confirmed = confirm(translate('confirmation.warning'));
        if (confirmed) {
            history.back();
        }
    };

    const handleDeleteDraft = () => {
        if (!order) {
            return;
        }

        deleteOrderMutation.mutate(order.id, {
            onSuccess: () => {
                const originLocation = location.state?.originLocation;
                if (originLocation) {
                    navigate({
                        to: originLocation.pathname,
                        search: originLocation.search,
                        state: originLocation.state,
                    });
                    return;
                }

                navigate({ to: '/orders', search: { tab: OrderTabEnum.DRAFTED } });
            },
        });
    };

    return (
        <Formik<FormikCreateEnquiryValues>
            initialValues={initialValues}
            validateOnChange={false}
            initialTouched={{
                receiverSupplierIds:
                    (isDraftEnquiry && initialValues.receiverSupplierIds?.length) || isCopiedFromOrder ? true : false,
                buyerReference: !!(isCopiedFromOrder && initialValues.buyerReference),
            }}
            validate={formikValidate}
            onSubmit={(values, formikHelpers) => {
                if (isPlacingDirectOrder || directOrderValues) {
                    formikHelpers.setSubmitting(false);
                    return;
                }
                if (values.orderMode === OrderMode.DIRECT_ORDER) {
                    formikHelpers.setSubmitting(false);
                    setDirectOrderValues(values);
                    return;
                }
                if (values.spot) {
                    formikHelpers.setSubmitting(false);
                    setSpotModalOpen(true);
                } else {
                    return handleSendEnquiry(values);
                }
            }}
        >
            {(formikProps) => {
                return (
                    <div>
                        <FormikCreateEnquiryForm
                            {...formikProps}
                            orderId={orderId}
                            orderSource={order?.source}
                            isPlacingDirectOrder={isPlacingDirectOrder}
                            isConfirmingDirectOrder={!!directOrderValues}
                            onDeleteDraft={canDeleteDraft ? handleDeleteDraft : undefined}
                            isDeletingDraft={deleteOrderMutation.isPending}
                            draftDeleteReference={order?.buyerReference}
                            onCloseClick={onCloseClick}
                        />

                        {directOrderValues ? (
                            <DirectOrderModal
                                values={directOrderValues}
                                isPending={isPlacingDirectOrder}
                                onDismiss={() => {
                                    if (!isPlacingDirectOrder) {
                                        setDirectOrderValues(null);
                                    }
                                }}
                                onConfirm={() => {
                                    const confirmedValues = directOrderValues;
                                    setDirectOrderValues(null);
                                    void placeDirectOrder(confirmedValues, {
                                        onSuccess: (offerId) => navigate({ to: '/offer/$id', params: { id: offerId } }),
                                        onError: async (message, enquiryId) => {
                                            addErrorToast(translate(message));
                                            if (enquiryId) {
                                                await navigate({ to: '/order/$id', params: { id: enquiryId } });
                                            }
                                        },
                                    });
                                }}
                            />
                        ) : null}

                        <FormikSpotModal
                            onSubmit={() => {
                                formikProps.setSubmitting(true);
                                void handleSendEnquiry(formikProps.values);
                            }}
                            isOpen={spotModalOpen}
                            onDismiss={() => setSpotModalOpen(false)}
                        />
                    </div>
                );
            }}
        </Formik>
    );
};
